import React, { useCallback, useMemo } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import useOffers from 'hooks/Offers/useOffers'
import useOffer from 'hooks/Offers/useOffer'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import FinePrintModal from '../../Chat/MessageMeta/FinePrintModal'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { useAppDispatch } from 'hooks/reduxHooks'

interface Props {
  order: App.Order;
  isPastOrder: boolean;
  hideSelect?: boolean;
  actions?: Array<App.ChatMessageMetaContent>;
  payload?: App.ChatMessageMetaPayload;
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>;
}

export default function BundleAndSavePurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap } = props

  const items = order.items

  const dispatch = useAppDispatch()

  const [childOffers, isFetchingOffers] = useOffers<App.Offer>(
    items?.map((item) => item.offer?.id),
  )

  const locationLabel = useMemo(
    () =>
      childOffers
        ?.map((offer) =>
          [offer.locationHeading, offer.locationSubheading].join(', '),
        )
        .join(' & '),
    [childOffers],
  )

  const propertyLabel = useMemo(
    () => childOffers?.map((offer) => offer.property?.name).join(' & '),
    [childOffers],
  )

  // TEMP until we do select item on bundle and save
  const item = items?.[0]
  const [offer, isFetchingItemOffer] = useOffer<App.BundleOffer>(item?.offerId)

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id } }))
  }, [dispatch, order.id])

  if (!offer || isFetchingOffers || isFetchingItemOffer) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS" />}>
              Hotel
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{locationLabel}</CaptionTextBlock>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {propertyLabel}
              </LesaPurchaseSummaryTitle>{' '}
              {order.items?.map((item) => (
                <LesaPurchaseSummaryItineraryDates
                  key={item.id}
                  status={item.status}
                  startDate={item.reservation?.startDate}
                  endDate={item.reservation?.endDate}
                  duration={item.package?.duration ? pluralizeToString('night', item.package?.duration) : undefined}
                  bookingNumber={item.bookingNumber}
                  showReservationDates={!!item.reservation?.startDate}
                />
              ))}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} kind="secondary" fit="mobile-full-width" size="small">
                Select
              </TextButton>
            }
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!componentsMap?.get('fine-print-modal') && (
        <FinePrintModal offer={offer} />
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}
